import { addDays, differenceInCalendarDays, format } from 'date-fns';

export type SeasonDay = {
  x: number; // Day offset from the season start in a non-leap reference calendar, so seasons align by month/day.
  d: string; // YYYY-MM-DD
  future: boolean;
  held?: boolean; // Loads held at today's values until the plan starts (drawn dotted).
  leap?: boolean; // Feb 29: kept for totals but not drawn, since it shares an x with Feb 28.
  tl: number;
  low: number;
  high: number;
  peak: number;
  ts: number; // Training status rating, 0..1 (5 stars = 1)
  sg: { tp: number | null; hie: number | null; pp: number | null }; // Predicted signature (hie in kJ)
};

export type Breakthrough = {
  value: number;
  path: string;
  name: string;
  date: string;
  date_label: string; // Activity's local date/time, e.g. "Tue, Jul 1st 2025 8:05:12 am"
  signature: { tp: number; hie: number; pp: number }; // Signature the activity produced (hie in kJ)
  changes: { tp: number; hie: number; pp: number } | null; // Signature change the activity caused
  mep: number | null; // Maximal effort, seconds
} | null;

/** An activity tagged Race or Event (Race when it has both), drawn as a target on the charts. */
export type SeasonEvent = {
  kind: 'Race' | 'Event';
  path: string;
  name: string;
  date: string; // YYYY-MM-DD
  date_label: string;
};

export type Season = {
  start: string;
  end: string;
  days: SeasonDay[];
  breakthroughs: { tp: Breakthrough; hie: Breakthrough; pp: Breakthrough };
  events?: SeasonEvent[];
  distance_km: number;
  hours: number;
  active_days: number;
  elapsed_days: number;
};

export type SeasonsResponse = {
  years_available: number[];
  // The year after the current one, when an XFAI forecast plan (Goal/Event/Race) reaches into its season; null
  // otherwise. Unlike years_available, this year has no real activity history - its days are wholly a projection.
  next_year_available: number | null;
  years: number[];
  seasons: Record<string, Season>;
};

export type Metric = 'tl' | 'low' | 'high' | 'peak';
export type SigKey = 'tp' | 'hie' | 'pp';
/** What the TP/HIE/PP cards plot: their training load, or the predicted signature value. */
export type ChartView = 'load' | 'signature';

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const STORAGE_KEY = 'mySeasons.settings';

/** Defaults: January to the current month, no checkpoint. */
export function defaultSettings() {
  return { startMonth: 1, endMonth: new Date().getMonth() + 1, checkpoint: null as number | null };
}

const VIEW_KEY = 'mySeasons.view';

/** The chart view from sessionStorage; Training Loads unless the user switched it this session. */
export function loadView(): ChartView {
  try {
    return sessionStorage.getItem(VIEW_KEY) === 'signature' ? 'signature' : 'load';
  } catch {
    return 'load';
  }
}

export function saveView(view: ChartView) {
  try {
    sessionStorage.setItem(VIEW_KEY, view);
  } catch {
    // Storage unavailable: the view just won't survive a reload.
  }
}

const validMonth = (m: unknown): m is number => Number.isInteger(m) && (m as number) >= 1 && (m as number) <= 12;

/**
 * The start/end months and checkpoint from sessionStorage. The checkpoint is a day offset that only means
 * something for its month range, so it is dropped if it falls outside the stored range. On first open
 * (nothing valid stored) the defaults are written back so the page reopens with the same settings.
 * Storage can throw or be unavailable (private windows, blocked site data), in which case the defaults
 * are used unsaved.
 */
export function loadSettings() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
    if (validMonth(stored?.startMonth) && validMonth(stored?.endMonth)) {
      const { max } = axisLayout(stored.startMonth, stored.endMonth);
      const x = stored.checkpoint;
      const checkpoint = Number.isInteger(x) && x >= 0 && x <= max ? (x as number) : null;
      return { startMonth: stored.startMonth as number, endMonth: stored.endMonth as number, checkpoint };
    }
  } catch {
    // Fall through to the defaults.
  }
  const defaults = defaultSettings();
  saveSettings(defaults.startMonth, defaults.endMonth, defaults.checkpoint);
  return defaults;
}

export function saveSettings(startMonth: number, endMonth: number, checkpoint: number | null) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ startMonth, endMonth, checkpoint }));
  } catch {
    // Storage unavailable: the settings just won't survive a reload.
  }
}

// Twenty well-separated hues, so seasons stay distinguishable and colours only repeat every 20 years.
// Neighbouring entries contrast strongly, so consecutive years never look alike. 2026 (index 6) is the navy rather
// than the magenta, which sat too close to the Race target's raspberry (see STANDARD_ACTIVITY_TAGS).
const YEAR_COLORS = [
  '#1f77b4', '#e6194b', '#2ca02c', '#ff7f0e', '#7b3fbf', '#17becf', '#1a237e', '#8c564b', '#b5a300', '#d81b9b',
  '#84cc16', '#c2185b', '#009e73', '#d55e00', '#56b4e9', '#6b7280', '#111827', '#a16207', '#5b21b6', '#0f766e',
];

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** Stable per-year colour so toggling other years doesn't recolour the rest. */
export function yearColor(year: number) {
  return YEAR_COLORS[mod(year, YEAR_COLORS.length)];
}

// Same non-leap reference calendar the server uses for SeasonDay.x.
const REF_YEAR = 2001;

/** Month boundaries on the shared x axis, plus its length in days. */
export function axisLayout(startMonth: number, endMonth: number) {
  const refStart = new Date(REF_YEAR, startMonth - 1, 1);
  const spansYears = startMonth > endMonth;
  const ticks: { x: number; label: string }[] = [];
  let month = startMonth;
  for (let i = 0; i < 12; i++) {
    const year = spansYears && month <= endMonth ? REF_YEAR + 1 : REF_YEAR;
    ticks.push({ x: differenceInCalendarDays(new Date(year, month - 1, 1), refStart), label: MONTHS[month - 1].slice(0, 3) });
    if (month === endMonth) break;
    month = (month % 12) + 1;
  }
  const endYear = spansYears ? REF_YEAR + 1 : REF_YEAR;
  const max = differenceInCalendarDays(new Date(endYear, endMonth, 0), refStart);
  return { ticks, max };
}

/** Month/day for an x offset on the shared axis, e.g. "Feb 10" (seasons align by calendar day, so it holds for every year). */
export function checkpointLabel(x: number, startMonth: number) {
  return format(addDays(new Date(REF_YEAR, startMonth - 1, 1), x), 'MMM d');
}

export const parseDay = (d: string) => new Date(`${d}T00:00:00`);
export const formatDay = (d: string) => format(parseDay(d), 'MMM d, yyyy');
export const formatMonthDay = (d: string) => format(parseDay(d), 'MMM d');

/** Days that are drawn/looked up by x: leap days are skipped. */
export function daysByX(season: Season): Map<number, SeasonDay> {
  const byX = new Map<number, SeasonDay>();
  for (const day of season.days) {
    if (!day.leap) byX.set(day.x, day);
  }
  return byX;
}

export function metricValue(day: SeasonDay, metric: Metric) {
  return day[metric];
}

/** Start / highest / end of a training load metric; the highest day is included for status lookups. */
export function loadStats(season: Season, metric: Metric) {
  const days = season.days;
  if (!days.length) return null;
  let high = days[0];
  for (const day of days) {
    if (day[metric] > high[metric]) high = day;
  }
  return { start: days[0], high, end: days[days.length - 1] };
}

/** Start / highest / end of the predicted signature value, or null when there is no seed for the parameter. */
export function signatureStats(season: Season, key: SigKey) {
  const days = season.days.filter((d) => d.sg[key] != null);
  if (!days.length) return null;
  let high = days[0];
  for (const day of days) {
    if ((day.sg[key] as number) > (high.sg[key] as number)) high = day;
  }
  return { start: days[0], high, end: days[days.length - 1] };
}

export function formatSignature(value: number | null | undefined, key: SigKey) {
  if (value == null) return '–';
  return key === 'hie' ? `${value.toFixed(1)} kJ` : `${Math.round(value)} W`;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** [hh:]mm:ss, as the XPMC chart's formatTime. */
export function formatEffort(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h > 0 ? `${pad2(h)}:` : ''}${pad2(m)}:${pad2(s)}`;
}

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/** The XPMC chart's breakthrough balloon: name, date, fitness measure and maximal effort; `kind` first when it is also a Race or Event. */
export function breakthroughBalloonHtml(bt: NonNullable<Breakthrough>, kind?: SeasonEvent['kind']) {
  return (
    (kind ? `<h5 style="margin:0 0 2px"><b>${kind}</b></h5>` : '') +
    `<h5 style="margin:0 0 2px"><b>${escapeHtml(bt.name)}</b></h5>` +
    `<h6 style="margin:0 0 4px">${escapeHtml(bt.date_label)}</h6>` +
    `<table class="text-center" style="margin:0 0 4px">${SIG_ROWS.map(({ key, label, icon, cls }) => {
      const change = bt.changes?.[key];
      const changeText = change == null ? '' : `${change > 0 ? '+ ' : change < 0 ? '- ' : ''}${Math.abs(change)}`;
      return (
        `<tr><th style="padding:2px 8px 2px 0;text-align:left"><i class="fa ${icon} ${cls}"></i> <span class="${cls}">${label}</span></th>` +
        `<td style="padding:2px 8px">${formatSignature(bt.signature[key], key).toLowerCase()}</td>` +
        `<td style="padding:2px 0 2px 8px">${changeText}</td></tr>`
      );
    }).join('')}</table>` +
    (bt.mep != null ? `<div>Maximal Effort: ${formatEffort(bt.mep)}</div>` : '')
  );
}

/** The balloon for a Race/Event target: the kind first, then the activity's name and date. */
export function eventBalloonHtml(event: SeasonEvent) {
  return (
    `<h5 style="margin:0 0 2px"><b>${event.kind}</b></h5>` +
    `<div style="margin:0 0 2px">${escapeHtml(event.name)}</div>` +
    `<h6 style="margin:0">${escapeHtml(event.date_label)}</h6>`
  );
}

// Rows of the balloon's signature table, in the order the activity page's breakthrough tooltip uses.
const SIG_ROWS = [
  { key: 'pp', label: 'PP', icon: 'fa-bolt', cls: 'peak-power' },
  { key: 'hie', label: 'HIE', icon: 'fa-line-chart', cls: 'atc' },
  { key: 'tp', label: 'TP', icon: 'fa-bolt', cls: 'tp' },
] as const;
